import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import MDTypography from "components/MDTypography";
import { getVehicleCheckLogs } from "store/slice/reservation/reservationSlice";
import MultiPlacePicker from "components/MDPlacePicker/MultiPlacePicker";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { InputAdornment, IconButton } from "@mui/material";
import { getTimezoneName } from "global/functions";
import {
  LinearProgress,
  Grid,
  Card,
  Box,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { useMaterialUIController } from "context";
import moment from "moment";
import { useMemo } from "react";

import MDDateRangePicker from "components/MDDateRagePicker";
import QRCodeComponent from "./components/QRCodeComponent";

const VehicleChecker = () => {
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { activityLogs, total, loading } = useSelector(
    (state) => state.reservations,
  );
  const { multiSelectedPlaces } = useSelector((state) => state.places);
  const { meInfo } = useSelector((state) => state.users);
  const [filterOptions, setFilterOptions] = useState({
    search: "",
    startDate: "",
    endDate: "",
    placeIds: [],
  });

  const [localSearch, setLocalSearch] = useState("");
  const role = meInfo?.role || meInfo?.roleId || meInfo?.roles?.[0];
  const isSuperAdmin = role?.level === 100;

  const selectedPlaceIds = useMemo(() => {
    return multiSelectedPlaces?.map((p) => p?._id).filter(Boolean) || [];
  }, [multiSelectedPlaces]);
  const placeFilter = useMemo(() => {
    return selectedPlaceIds.includes("all") ? [] : selectedPlaceIds;
  }, [selectedPlaceIds]);

  const currentTimeZone = getTimezoneName();
  useEffect(() => {
    if (!isSuperAdmin || !meInfo) return;
    const delayDebounceFn = setTimeout(() => {
      const payload = {
        search: filterOptions.search,
        page,
        limit: pageSize,
        placeIds: placeFilter.join(","),
        timeZone: currentTimeZone,
      };

      if (filterOptions.startDate) {
        payload.startDate = moment(filterOptions.startDate).format(
          "YYYY-MM-DD",
        );
      }
      if (filterOptions.endDate) {
        payload.endDate = moment(filterOptions.endDate).format("YYYY-MM-DD");
      }

      dispatch(getVehicleCheckLogs(payload));
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [dispatch, filterOptions, pageSize, placeFilter, page]);

  const handleManualSearch = (filters = filterOptions) => {
    setPage(1);
    const payload = {
      search: filters.search,
      page: 1,
      limit: pageSize,
      placeIds: placeFilter,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (filterOptions.startDate)
      payload.startDate = moment(filterOptions.startDate).format("YYYY-MM-DD");
    if (filterOptions.endDate)
      payload.endDate = moment(filterOptions.endDate).format("YYYY-MM-DD");

    dispatch(getVehicleCheckLogs(payload));
  };
  const dateToShow = (date) => {
    if (!date) return "N/A";
    return moment(date).format("MM/DD/YYYY hh:mm A");
  };

  if (!meInfo) return null;

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#f0f2f5"
        >
          <QRCodeComponent />
        </Box>
      </DashboardLayout>
    );
  }
  const columns = [
    {
      field: "licensePlate",
      headerName: "License Plate",
      width: 180,
      renderCell: ({ row }) => (
        <MDTypography variant="caption">
          {row?.licensePlate || "N/A"}
        </MDTypography>
      ),
    },

    {
      field: "parkingCode",
      headerName: "Parking Code",

      width: 180,
      renderCell: (params) => {
        const places = Array.isArray(params.row.placeIds)
          ? params.row.placeIds
          : params.row.placeIds
            ? [params.row.placeIds]
            : [];

        return (
          <>
            {places.map((p, i) => {
              const code = p?.parkingCode || "N/A";

              return (
                <Tooltip key={i} title={code}>
                  <MDTypography variant="caption">
                    {code}
                    {i !== places.length - 1 && ", "}
                  </MDTypography>
                </Tooltip>
              );
            })}
          </>
        );
      },
    },
    {
      field: "email",
      headerName: "Checked By",
      width: 260,
      renderCell: ({ row }) => {
        const displayEmail = row.email || row.userId?.email || "N/A";
        return <MDTypography variant="caption">{displayEmail}</MDTypography>;
      },
    },

    {
      field: "createdAt",
      headerName: "Timestamp",
      type: "date",
      width: 250,
      valueGetter: (params) =>
        params?.row?.createdAt ? new Date(params.row.createdAt) : null,

      renderCell: (params) => (
        <MDTypography variant="caption" color="text">
          {dateToShow(params.row.createdAt)}
        </MDTypography>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card>
        <MDBox pt={2} pb={3}>
          <MDBox
            mx={1}
            mt={-2}
            py={1}
            px={1}
            variant="gradient"
            bgColor={sidenavColor}
            borderRadius="lg"
            coloredShadow={sidenavColor}
          >
            <MDTypography variant="h6" color="white">
              Vehicle Check Activity Logs
            </MDTypography>
          </MDBox>
          <Box p={3}>
            <Grid container spacing={1} alignItems="stretch">
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <MultiPlacePicker notDefaultPlace={true} isDashboard={true} />
                  <MDDateRangePicker
                    filterOptions={filterOptions}
                    setFilterOptions={setFilterOptions}
                  />
                  <TextField
                    fullWidth
                    label="Search"
                    variant="outlined"
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const updatedFilters = {
                          ...filterOptions,
                          search: localSearch,
                        };

                        setFilterOptions(updatedFilters);
                        handleManualSearch(updatedFilters);
                      }
                    }}
                    InputProps={{
                      sx: { pr: 0, height: "45px" },
                      endAdornment: (
                        <InputAdornment position="end" sx={{ height: "100%" }}>
                          {/* CROSS ICON */}
                          {localSearch && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setLocalSearch("");
                                setFilterOptions({
                                  ...filterOptions,
                                  search: "",
                                });

                                dispatch(
                                  getVehicleCheckLogs({
                                    search: "",
                                    page: 1,
                                    limit: pageSize,
                                    placeIds: placeFilter,
                                    timeZone:
                                      Intl.DateTimeFormat().resolvedOptions()
                                        .timeZone,
                                  }),
                                );
                              }}
                              sx={{ mr: 1 }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}

                          <Box
                            sx={{
                              width: "1px",
                              height: "45PX",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            }}
                          />

                          <IconButton
                            onClick={() => {
                              setFilterOptions({
                                ...filterOptions,
                                search: localSearch,
                              });
                              handleManualSearch();
                            }}
                            sx={{
                              backgroundColor: sidenavColor,

                              borderRadius: "0 8px 8px 0",
                              height: "100%",
                              width: "48px",
                              ml: "1px",
                              "&:hover": { backgroundColor: sidenavColor },
                            }}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={4}>
                <QRCodeComponent />
              </Grid>
            </Grid>
          </Box>

          <Box p={3}>
            <MDDataGrid
              rows={activityLogs || []}
              columns={columns}
              getRowId={(row) => row._id}
              loading={loading}
              autoHeight
              pagination
              paginationMode="server"
              rowCount={total}
              paginationModel={{
                page: page - 1,
                pageSize: pageSize,
              }}
              onPaginationModelChange={(model) => {
                console.log("MODEL", model);

                setPage(model.page + 1);
                setPageSize(model.pageSize);
              }}
              pageSizeOptions={[25, 50, 100]}
              components={{
                NoRowsOverlay: EmptyTableRowOverlay,
                LoadingOverlay: LinearProgress,
              }}
            />
          </Box>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default VehicleChecker;
